import { useSearchParams } from 'react-router-dom';
import { useAuthStore, useDrawerStore } from '../../../store';
import {
  useDeleteData,
  useFetchData,
  useUnitOfMeasureStore,
} from '../../../hooks';
import React, { useEffect, useState } from 'react';
import { PageableResponse, UnitOfMeasureModel } from '../../../types';
import { Resource } from '../../../lib/enums';
import {
  convertDate,
  dateTimeFormat,
  roundAndFormat,
} from '../../../lib/utils';
import { Spin } from 'antd';
import BaseTable from '../../../components/BaseTable';
import InfoModal from '../../../components/ui/modal/InfoModal';

const UnitOfMeasureTable = ({
  api,
  moduleName,
  moduleCode,
}: {
  api: string;
  moduleName: string;
  moduleCode: string;
}) => {
  const [searchParams] = useSearchParams();
  const drawerStore = useDrawerStore();
  const unitOfMeasureStore = useUnitOfMeasureStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const { user } = useAuthStore();
  const userModuleAccess = user?.moduleAccess?.find(
    (item) => item.module.code === moduleCode,
  );

  const { data, isLoading } = useFetchData<
    PageableResponse<UnitOfMeasureModel>
  >(api, {
    resource: searchParams.get('resource') || Resource.FULL,
    search: searchParams.get('search') || '',
    length: parseInt(searchParams.get('length') || '999999'),
  });

  const {
    mutate,
    isPending: isPendingDelete,
    isSuccess: isSuccessDelete,
  } = useDeleteData(api);
  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Unit Type',
      dataIndex: 'unitType',
    },
    {
      title: 'Conversion Type',
      dataIndex: 'conversionType',
    },
    {
      title: 'Base UoM',
      dataIndex: 'baseUom',
      render: (_text: string, record: UnitOfMeasureModel) =>
        `${record?.baseUom?.description === undefined ? '' : record.baseUom?.description}`,
    },
    {
      title: 'Count',
      dataIndex: 'count',
      render: (_text: string, record: UnitOfMeasureModel) =>
        `${roundAndFormat(record?.count)}`,
    },
    {
      title: 'Factor',
      dataIndex: 'factor',
      render: (_text: string, record: UnitOfMeasureModel) =>
        `${roundAndFormat(record?.factor)}`,
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      render: (_text: string, record: UnitOfMeasureModel) =>
        `${roundAndFormat(record?.rate)}`,
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (_text: string, record: UnitOfMeasureModel) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.createdBy}</span>
            {record.timeCreated != null && (
              <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Update By/Date',
      dataIndex: 'updatedBy',
      render: (_text: string, record: UnitOfMeasureModel) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.updatedBy}</span>
            {record.timeUpdated != null && (
              <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
  ];

  const handleDataDelete = (data: UnitOfMeasureModel) => {
    mutate({ id: data.id });
    setSpinning(true);
  };

  const handleDataEdit = (data: UnitOfMeasureModel) => {
    unitOfMeasureStore.setData(data);
    drawerStore.onClose();
  };

  useEffect(() => {
    if (isSuccessDelete) setSpinning(false);
  }, [isSuccessDelete]);

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <BaseTable
        dataSource={data?.data || []}
        isLoading={isLoading || isPendingDelete}
        handleDeleteButtonClick={handleDataDelete}
        handleEditButtonClick={handleDataEdit}
        columns={columns}
        canDelete={userModuleAccess?.delete}
        canEdit={userModuleAccess?.write}
        userRole={user?.role}
        module={moduleName}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={`${moduleName} successfully deleted`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default UnitOfMeasureTable;
